
function cosineSimilarity(vectorPose1XY, vectorPose2XY) {
  let v1DotV2 = 0;
  let absV1 = 0;
  let absV2 = 0;

  vectorPose1XY.forEach((v1, index) => {
    const v2 = vectorPose2XY[index];
    v1DotV2 += v1 * v2;
    absV1 += v1 * v1;
    absV2 += v2 * v2;
  });
  absV1 = Math.sqrt(absV1);
  absV2 = Math.sqrt(absV2);

  return v1DotV2 / (absV1 * absV2);
}

function cosineDistanceMatching(vectorPose1XY, vectorPose2XY) {
  const cosSimilarity = cosineSimilarity(vectorPose1XY, vectorPose2XY);

  return Math.sqrt(2 * (1 - cosSimilarity));
}

function weightedDistanceMatching(vectorPose1XY, vectorPose2XY, vectorConfidences) {
  const summation1 = 1 / vectorConfidences[vectorConfidences.length - 1];

  let summation2 = 0;
  for (let i = 0; i < vectorPose1XY.length; i++) {
    let confIndex = Math.floor(i / 2);
    summation2 += vectorConfidences[confIndex] * Math.abs(vectorPose1XY[i] - vectorPose2XY[i]);
  }

  return summation1 * summation2;
}

function vectorizeAndNormalize(pose, options) {
  let [vectorPoseXY, vecotPoseTransform, vectorPoseConfidences] = convertPoseToVectors(pose, options.customWeight);

  vectorPoseXY = scaleAndTranslate(vectorPoseXY, vecotPoseTransform);

  vectorPoseXY = l2Normalization(vectorPoseXY);

  return [
    vectorPoseXY,
    vectorPoseConfidences
  ];
}

function convertPoseToVectors(pose, weightOption) {
  let vectorPoseXY = [];

  let translateX = Number.POSITIVE_INFINITY;
  let translateY = Number.POSITIVE_INFINITY;
  let scaler = Number.NEGATIVE_INFINITY;

  let vectorScoresSum = 0;
  let vectorScores = [];

  // get weightOption if exists
  let mode, scores;
  if (weightOption) {
    mode = weightOption.mode;
    if (!mode || typeof mode !== 'string') throw new TypeError(`[Bad customWeight option] A mode must be specified and should be either 'multiply', 'replace' or 'add'.`);

    scores = weightOption.scores;
    if (typeof scores !== 'object' && !Array.isArray(scores)) {
      throw new TypeError(`[Bad customWeight option] scores must be Object or Number[].Please refer the document https://github.com/freshsomebody/posenet-similarity to set it correctly.`);
    }
  }

  pose.keypoints.forEach((point, index) => {
    const x = point.x;
    const y = point.y;

    vectorPoseXY.push(x, y);

    translateX = Math.min(translateX, x);
    translateY = Math.min(translateY, y);
    scaler = Math.max(scaler, Math.max(x, y));

    let score = point.score;
    // modify original score according to the weightOption
    if (mode && scores) {
      let scoreModifier = false;
      // try to get scores from the weightOption
      if (scores[point.name] || scores[point.name] === 0) scoreModifier = scores[point.name];
      if (scores[index] || scores[index] === 0) scoreModifier = scores[index];

      // manipulate the original score
      if ((scoreModifier || scoreModifier === 0) && typeof scoreModifier === 'number') {
        switch (mode) {
          case 'multiply':
            score *= scoreModifier;
            break;
          case 'replace':
            score = scoreModifier;
            break;
          case 'add':
            score += scoreModifier;
            break;
          default:
            throw new Error(`[Bad customWeight option] A mode must be specified and should be either 'multiply', 'replace' or 'add'`);
        }
      }
    }

    vectorScoresSum += score;
    vectorScores.push(score);
  });
  vectorScores.push(vectorScoresSum);

  return [
    vectorPoseXY,
    [translateX / scaler, translateY / scaler, scaler],
    vectorScores
  ];
}

function scaleAndTranslate(vectorPoseXY, transformValues) {
  const [transX, transY, scaler] = transformValues;

  return vectorPoseXY.map((position, index) => {
    return (index % 2 === 0 ?
      position / scaler - transX :
      position / scaler - transY);
  });
}

function l2Normalization(vectorPoseXY) {
  let absVectorPoseXY = 0;
  vectorPoseXY.forEach((position) => {
    absVectorPoseXY += Math.pow(position, 2);
  });
  absVectorPoseXY = Math.sqrt(absVectorPoseXY);

  return vectorPoseXY.map((position) => {
    return position / absVectorPoseXY;
  });
}

export function poseSimilarity(pose1, pose2, overridenOptions) {
  // check inputted poses
  if (
    !pose1 || !pose1.keypoints ||
    !pose2 || !pose2.keypoints
  ) {
    throw new Error('[Bad pose parameters] Please check your pose objects again.');
  }

  if (pose1.keypoints.length === 0 || pose2.keypoints.length === 0) {
    throw new Error('[Bad pose parameters] Found pose object(s) with empty keypoint.');
  }

  if (pose1.keypoints.length !== pose2.keypoints.length) {
    throw new Error('[Bad pose parameters] The keypoint lengths of the two pose objects are not the same.');
  }

  // merge options
  const defaultOptions = {
    strategy: 'weightedDistance'
  };
  const options = Object.assign({}, defaultOptions, overridenOptions);

  let [vectorPose1XY, vectorPose1Scores] = vectorizeAndNormalize(pose1, options);
  let [vectorPose2XY] = vectorizeAndNormalize(pose2, options);

  // execute strategy
  // if strategy is given by the string form
  if (typeof options.strategy === 'string') {
    switch (options.strategy) {
      case 'cosineSimilarity':
        return cosineSimilarity(vectorPose1XY, vectorPose2XY);
      case 'cosineDistance':
        return cosineDistanceMatching(vectorPose1XY, vectorPose2XY);
      case 'weightedDistance':
        return weightedDistanceMatching(vectorPose1XY, vectorPose2XY, vectorPose1Scores);
      default:
        throw new Error(`[Bad strategy option] It should be either 'cosineSimilarity', 'cosineDistance' or 'weightedDistance' (default).`);
    }
    // if strategy is given by a custom function
  } else if (typeof options.strategy === 'function') {
    return options.strategy(vectorPose1XY, vectorPose2XY, vectorPose1Scores);
  } else {
    throw new TypeError(`[Bad strategy option] It only accepts string or function types of values.`);
  }
}
